import { GetServerSideProps } from 'next';
import { api, mimir, site } from '@/services/api';
import { FeaturedNewsApi } from '@/pages/ultimas';
import { HeadTags, NavBarContentData, Segmentations, TimeLineApi } from '@/shared/types/home.types';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { useRouter } from 'next/router';
import Layout from '@/components/Layout';
import dynamic from 'next/dynamic';
import { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import { SchemaType } from '@/components/Internals/InternalSchema';
import { NavBarContent } from '@/components/Navbar';
import FeaturedLastNews from '@/components/Home/FeaturedLastNews';
import PlaylistHeader from '@/components/Listing/PlaylistHeader';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import WrapperContent from '@/components/Layout/WrapperContent';

export interface LastVideosPlaylistData {
  status: 0;
  data: {
    titulo: string;
    descricao: string;
    breadcrumb: BreadcrumbItem[];
  };
  ssr: HeadTags[];
  segmentacoes: Segmentations[];
  schema?: SchemaType;
}

export interface LastVideosPlaylistProps {
  homeData: LastVideosPlaylistData;
  navBarData: NavBarContent;
  tagsData: LastVideosPlaylistData['ssr'];
  segmentacoes: LastVideosPlaylistData['segmentacoes'];
  timelineData: TimeLineApi['data'];
}

export default function LastVideosPlaylist({ homeData, navBarData, tagsData, segmentacoes, timelineData }: LastVideosPlaylistProps) {
  const router = useRouter();
  const { schema } = homeData;
  const { titulo, descricao, breadcrumb } = homeData.data;
  const { canais } = router.query;
  const AdsTypeGenerators = dynamic(() => import('@/components/Ads/Google/AdsTypeGenerators'), { ssr: false });

  return (
    <Layout layoutData={{ navBarData, tagsData, segmentacoes, schema }}>
      <PlaylistHeader titulo={titulo} descricao={descricao} breadcrumb={breadcrumb} />
      <WrapperContent desktopOnly>
        <AdContainer noPadding billboard style={{ background: 'transparent' }}>
          <AdsTypeGenerators type="billboard/main/listing" />
        </AdContainer>
      </WrapperContent>
      <FeaturedLastNews
        featuredLastNewsData={timelineData?.timeline}
        rootPage={`video/playlists/${canais}`}
        textTitle="Últimos Vídeos"
        headingTag="h2"
        routeType="tipo"
        route={`video/${canais}`}
      />
    </Layout>
  );
}
export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;
  const { canais } = context.query;

  const mainReq = { promise: site.get<FeaturedNewsApi>(`/video/playlists/${canais}/`), link: `/video/playlists/${canais}/` };
  const timelineReq = { promise: mimir.get<TimeLineApi>(`/timelines/ultimas/tipo/video/${canais}/`), link: `/timelines/ultimas/tipo/video/${canais}/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    timelinePromise: timelineReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const homeData = data;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const tagsData = data.ssr;
  const { segmentacoes } = data;
  const timelineData = promiseData[timelineReq.link].data.data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      tagsData,
      homeData,
      navBarData,
      segmentacoes,
      timelineData,
    },
  };
};
